import '@fortawesome/fontawesome-free/css/all.css';
import CssBaseline from '@mui/material/CssBaseline';
import { alpha } from '@mui/material/styles';
import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';

import type { Direction } from '@mui/material/styles';
import {
  createTheme,
  StyledEngineProvider,
  ThemeProvider,
} from '@mui/material/styles';

import { gray, mixins, shadows, shape, typography } from './themePrimitives';

/**
 * Auth theme color constants - exported for use in Layout components
 */
export const authColors = {
  primary: '#42474d', // Dark charcoal (rgb(66, 71, 77))
  primaryDark: '#363a3f',
  primaryLight: '#5a5f6e',
  secondary: '#ff9200', // Orange accent
  formBackground: '#f8f9fa', // Light gray for form panel
  inputBorder: '#e5e7eb',
  inputBorderHover: '#d1d5db',
  inputPlaceholder: '#9ca3af',
  panelText: '#9ca3af', // Text on dark panel
  panelTextMuted: 'rgba(255, 255, 255, 0.5)',
};

/**
 * Custom theme for Auth pages with dark charcoal primary buttons
 * matching the prototype design at https://sharp-login-look.lovable.app/
 */
export default function AuthTheme({ children }: PropsWithChildren) {
  const theme = useMemo(
    () =>
      createTheme({
        direction: 'ltr' as Direction,
        mixins,
        shape,
        typography,
        shadows,
        palette: {
          mode: 'light',
          primary: {
            main: authColors.primary,
            light: authColors.primaryLight,
            dark: authColors.primaryDark,
            contrastText: '#ffffff',
          },
          secondary: {
            main: authColors.secondary,
          },
          text: {
            primary: gray[800],
            secondary: gray[600],
          },
          background: {
            default: authColors.formBackground,
            paper: '#ffffff',
          },
          divider: authColors.inputBorder,
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: 8,
              },
              contained: {
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: 'none',
                },
              },
              outlined: ({ theme }) => ({
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
                '&:hover': {
                  borderColor: authColors.inputBorderHover,
                  backgroundColor: alpha(theme.palette.divider, 0.3),
                },
              }),
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: ({ theme }) => ({
                '& .MuiOutlinedInput-root': {
                  borderRadius: 8,
                  backgroundColor: theme.palette.background.paper,
                  '& fieldset': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover fieldset': {
                    borderColor: authColors.inputBorderHover,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 1,
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '12px 14px',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: authColors.inputPlaceholder,
                  opacity: 1,
                },
              }),
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: ({ theme }) => ({
                color: theme.palette.text.primary,
                fontWeight: 500,
                fontSize: '0.875rem',
                marginBottom: 6,
                position: 'relative',
                transform: 'none',
                '&.Mui-focused': {
                  color: theme.palette.text.primary,
                },
              }),
            },
          },
          MuiFormLabel: {
            styleOverrides: {
              root: ({ theme }) => ({
                color: theme.palette.text.primary,
                fontWeight: 500,
                fontSize: '0.875rem',
              }),
            },
          },
          MuiCheckbox: {
            styleOverrides: {
              root: ({ theme }) => ({
                color: authColors.inputBorderHover,
                '&.Mui-checked': {
                  color: theme.palette.primary.main,
                },
              }),
            },
          },
          MuiLink: {
            styleOverrides: {
              root: ({ theme }) => ({
                color: theme.palette.primary.main,
                textDecoration: 'none',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }),
            },
          },
          MuiDivider: {
            styleOverrides: {
              root: ({ theme }) => ({
                '&::before, &::after': {
                  borderColor: theme.palette.divider,
                },
              }),
            },
          },
          MuiTypography: {
            styleOverrides: {
              root: ({ theme }) => ({
                color: theme.palette.text.primary,
              }),
            },
          },
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                backgroundColor: authColors.formBackground,
              },
            },
          },
        },
      }),
    [],
  );

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
